
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { validateDashboardPassword, setDashboardSession } from "@/utils/dashboardAuth";
import { KeyRound, Loader2 } from "lucide-react";

interface LoginProps {
  onLoginSuccess: () => void;
}

const Login = ({ onLoginSuccess }: LoginProps) => {
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const isValid = await validateDashboardPassword(password);
      
      if (isValid) {
        setDashboardSession(rememberMe);
        toast({
          title: "Login successful",
          description: "Welcome to the Leads Dashboard.",
        });
        onLoginSuccess();
      } else {
        setError("Invalid password. Please try again.");
        toast({
          variant: "destructive",
          title: "Login failed",
          description: "Invalid password. Please try again.",
        });
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      toast({
        variant: "destructive",
        title: "Login failed",
        description: "An error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-6 p-6 glassmorphism dark:glassmorphism-dark rounded-xl">
      <div className="space-y-2 text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-desert-sunset/10 rounded-full p-3">
            <KeyRound className="w-6 h-6 text-desert-sunset" />
          </div>
        </div>
        <h1 className="text-2xl font-bold">Leads Dashboard</h1>
        <p className="text-muted-foreground">Enter your password to access the dashboard</p>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="remember-me"
            checked={rememberMe}
            onCheckedChange={(checked) => setRememberMe(checked === true)}
          />
          <Label
            htmlFor="remember-me"
            className="text-sm font-medium leading-none cursor-pointer"
          >
            Remember me for 24 hours
          </Label>
        </div>

        <Button
          type="submit"
          className="w-full bg-desert-gradient hover:opacity-90"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Logging in...
            </>
          ) : (
            "Login"
          )}
        </Button>
      </form>
    </div>
  );
};

export default Login;
